/**
 * Btn — primary interactive element using design token system.
 * Variants: primary | secondary | ghost | danger
 * Sizes: sm | md | lg
 */
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';

const cls = (...parts: (string | false | undefined | null)[]) =>
  parts.filter(Boolean).join(' ');

export type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type BtnSize = 'sm' | 'md' | 'lg';

const VARIANTS: Record<BtnVariant, string> = {
  primary:
    'bg-brand text-white hover:bg-brand-dark active:bg-brand-dark shadow-sm disabled:bg-ink-muted/30 disabled:text-ink-muted',
  secondary:
    'bg-surface border border-border text-ink hover:bg-surface-raised disabled:text-ink-muted',
  ghost:
    'bg-transparent border border-border text-ink hover:bg-surface-raised disabled:text-ink-muted',
  danger:
    'bg-neg text-white hover:bg-red-700 active:bg-red-800 shadow-sm disabled:bg-ink-muted/30 disabled:text-ink-muted',
};

const SIZES: Record<BtnSize, string> = {
  sm: 'min-h-[36px] px-3 py-1.5 text-small rounded-md gap-1.5',
  md: 'min-h-[44px] px-4 py-2 text-body rounded-md gap-2',
  lg: 'min-h-[48px] px-5 py-2.5 text-body rounded-card gap-2',
};

export interface BtnProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BtnVariant;
  size?: BtnSize;
  loading?: boolean;
}

export const Btn = forwardRef<HTMLButtonElement, BtnProps>(
  ({ variant = 'primary', size = 'md', loading, disabled, children, className, ...rest }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={cls(
        'inline-flex items-center justify-center font-ui font-medium transition-colors',
        'disabled:cursor-not-allowed',
        SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span
          aria-hidden
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        children
      )}
    </button>
  ),
);
Btn.displayName = 'Btn';
